import { useAuth0 } from '@auth0/auth0-react';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identifySentry, identifyPosthog, initLogrocket } from 'utils/tracking';

export default function useAuth() {
  const { hash, search } = useLocation();
  const posthog = usePostHog();
  const [actionableError, setActionableError] = useState('');

  const {
    isLoading,
    isAuthenticated,
    user: auth0User,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const { profile: user } = useCurrentUser();

  useEffect(() => {
    if (hash === '#refresh') {
      user.refetch();
    }
  }, [hash]);

  useEffect(() => {
    if (!user.isError) return;
    const organization = localStorage.getItem('organizationId');
    loginWithRedirect({
      authorizationParams: organization ? { organization } : undefined,
      appState: {
        returnTo: window.location.pathname + window.location.search,
      },
    });
  }, [user.isError]);

  useEffect(() => {
    if (!auth0User?.org_id || !user.data?.auth0OrgId) return;

    if (auth0User.org_id) {
      localStorage.setItem('organizationId', auth0User.org_id);
    }

    if (auth0User.org_id !== user.data.auth0OrgId) {
      user.refetch();
    }
  }, [auth0User?.org_id, user.data?.auth0OrgId]);

  useEffect(() => {
    if (!user.data?._id) return;

    initLogrocket(user.data);
    identifySentry(user.data);
    identifyPosthog(user.data, posthog);
  }, [user.data?.firm?._id, user.data?._id, posthog]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const error = params.get('error');
    const description = params.get('error_description');
    const isAccessDeniedError = error === 'access_denied';
    const isInvalidRequestError = error === 'invalid_request';

    if (isAccessDeniedError || isInvalidRequestError) {
      localStorage.removeItem('organizationId');
      setActionableError(description ?? 'Access denied');
      return;
    }

    if (
      !isLoading &&
      !isAuthenticated &&
      !isAccessDeniedError &&
      !isInvalidRequestError
    ) {
      const organization = localStorage.getItem('organizationId');
      loginWithRedirect({
        authorizationParams: organization ? { organization } : undefined,
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
    }
  }, [isLoading, isAuthenticated, search]);

  return {
    realm: user.data?.realm,
    isLoading: isLoading || !user.data?.realm,
    actionableError,
    loginWithRedirect,
    logout,
    user,
  };
}
